import { getValuation } from 'ClientPortal/src/api/valuationRequests'
import type {
    ClientPortalOrderOverview as OrderOverview,
    ClientPortalOrderOverviews as OrderOverviews,
} from '@einwert/valuation-api-client'
import type { ExistingOrder, NewOrder } from '@einwert/valuation-api-client'
import { QueryKey, skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getValuationApiClient } from 'ClientPortal/src/api/valuationApiClient'
import { getSelectedCustomer } from 'ClientPortal/src/context/SelectedCustomerContext'
import { useTypedParams } from 'ClientPortal/src/router/useTypedParams'
import { THREAD_KEYS } from 'ClientPortal/src/api/Queries/threads-keys'
import { useGlobalMutationErrorHandler } from 'ClientPortal/src/api/useGlobalMutationErrorHandler'
import { Operation } from 'fast-json-patch'
import { applyPatch, ReactQueryOptions, sendToSentry, toIsoDateString, useUploadMultiple, WithReactQueryOptions } from 'ComponentLibrary'
import { DateRange, PortfolioMetadata } from 'ClientPortal/src/pages/Orders/OrderList/OrderListPageContent'
import { Dayjs } from 'dayjs'
import { UUID } from 'ComponentLibrary/src/common/uuid'
import invariant from 'tiny-invariant'
import type { ExternalParticipantList, ExternalParticipantWithProxyEmail } from '@einwert/valuation-api-client'

export const CREATE_ORDER_KEY = 'CREATE_ORDER'
const ORDERS_KEY = 'ORDERS'
export const ORDERS_WITH_PLAUSIBILITY_KEY = 'ORDERS_WITH_PLAUSIBILITY'
export const ORDER_OVERVIEWS_KEY = 'ORDER_OVERVIEWS'
const EXTERNAL_PARTICIPANTS_KEY = 'EXTERNAL_PARTICIPANTS'
const ORDER_OVERVIEWS_INVALIDATION_DELAY_MS = 5_000
let orderOverviewsInvalidationTimer: ReturnType<typeof setTimeout> | undefined

export function useGetOrders({
    assetId,
    enabled = true,
    statuses,
    assignedCustomers,
    titleFilter,
    valuationDateRange,
    portfolio,
}: {
    assetId?: string
    statuses?: string[]
    assignedCustomers?: number[]
    titleFilter?: string
    valuationDateRange?: DateRange
    portfolio?: PortfolioMetadata
} & Pick<ReactQueryOptions, 'enabled'> = {}) {
    const filters = {
        assetId,
        statuses,
        assignedCustomers,
        titleFilter,
        valuationDateRange: valuationDateRange && {
            start: valuationDateRange.start?.toDate(),
            end: valuationDateRange.end?.toDate(),
        },
        portfolioId: portfolio?.uuid,
    }

    const joinedStatuses = statuses ? statuses.join(',') : undefined
    const joinedAssignedCustomers = assignedCustomers ? assignedCustomers.join(',') : undefined

    const queryKey: string[] = [ORDERS_KEY]

    assetId && queryKey.push(`assetId: ${assetId}`)

    joinedStatuses && queryKey.push(`statusFilter: ${joinedStatuses}`)
    joinedAssignedCustomers && queryKey.push(`assignedCustomerFilter: ${joinedAssignedCustomers}`)
    titleFilter && queryKey.push(`titleFilter: ${titleFilter}`)
    valuationDateRange &&
        queryKey.push(`valuationDateRange: ${dateOrEmptyString(valuationDateRange.start)}-${dateOrEmptyString(valuationDateRange.end)}`)
    portfolio && queryKey.push(`portfolio: ${portfolio.uuid}`)

    const { data, isPending, isLoading } = useQuery({
        // eslint-disable-next-line @tanstack/query/exhaustive-deps
        queryKey,
        queryFn: ({ signal }) =>
            getValuationApiClient().orders.getOrders({
                filters,
                customerId: getSelectedCustomer()?.uuid,
                options: { signal },
            }),
        enabled,
    })

    const filteredData = data ? data.orders : []

    return {
        orders: filteredData,
        isOrdersPending: isPending,
        isOrdersInitialLoading: isLoading,
    }
}

export function useGetOrder({ orderId, ...options }: WithReactQueryOptions<{ orderId?: string }>) {
    const queryKey = [ORDERS_KEY, orderId]

    const { data, isLoading } = useQuery({
        queryKey,
        queryFn: orderId
            ? ({ signal }) => getValuationApiClient().orders.getOrder(orderId, { customerId: getSelectedCustomer()?.uuid, signal })
            : skipToken,
        ...options,
    })

    return {
        order: data,
        isOrderInitialLoading: isLoading,
    }
}

export function useGetOrdersWithPlausibility({
    assetId,
    enabled = true,
    statuses,
    assignedCustomers,
    titleFilter,
    valuationDateRange,
    portfolio,
}: {
    assetId?: string
    statuses?: string[]
    assignedCustomers?: number[]
    titleFilter?: string
    valuationDateRange?: DateRange
    portfolio?: PortfolioMetadata
} & Pick<ReactQueryOptions, 'enabled'> = {}) {
    const filters = {
        assetId,
        statuses,
        assignedCustomers,
        titleFilter,
        valuationDateRange: valuationDateRange && {
            start: valuationDateRange.start?.toDate(),
            end: valuationDateRange.end?.toDate(),
        },
        portfolioId: portfolio?.uuid,
    }

    const joinedStatuses = statuses ? statuses.join(',') : undefined
    const joinedAssignedCustomers = assignedCustomers ? assignedCustomers.join(',') : undefined

    const queryKey: string[] = [ORDERS_WITH_PLAUSIBILITY_KEY]

    assetId && queryKey.push(`assetId: ${assetId}`)
    joinedStatuses && queryKey.push(`statusFilter: ${joinedStatuses}`)
    joinedAssignedCustomers && queryKey.push(`assignedCustomerFilter: ${joinedAssignedCustomers}`)
    titleFilter && queryKey.push(`titleFilter: ${titleFilter}`)
    valuationDateRange &&
        queryKey.push(`valuationDateRange: ${dateOrEmptyString(valuationDateRange.start)}-${dateOrEmptyString(valuationDateRange.end)}`)
    portfolio && queryKey.push(`portfolio: ${portfolio.uuid}`)

    const { data, isPending, isLoading } = useQuery({
        // eslint-disable-next-line @tanstack/query/exhaustive-deps
        queryKey,
        queryFn: ({ signal }) =>
            getValuationApiClient().orders.getOrdersWithPlausibility({
                filters,
                customerId: getSelectedCustomer()?.uuid,
                options: { signal },
            }),
        enabled,
    })

    const filteredData = data ? data.orders : []

    return {
        orders: filteredData,
        isOrdersPending: isPending,
        isOrdersInitialLoading: isLoading,
    }
}

export function useGetOrderOverview(options?: ReactQueryOptions) {
    const { orderId } = useTypedParams(['orderId'])

    return useGetOrderOverviewByOrderId(orderId, options)
}

export function useGetOrderOverviewByOrderId(orderId: UUID | undefined, options?: ReactQueryOptions) {
    const valuationApiClient = getValuationApiClient()
    const { data, isLoading } = useQuery({
        queryKey: [ORDER_OVERVIEWS_KEY, orderId],
        queryFn: orderId
            ? ({ signal }) => {
                  invariant(orderId, 'Cannot fetch order overview without orderId')
                  return valuationApiClient.orderOverviews.getClientPortalOrderOverview(
                      { orderId, customerId: getSelectedCustomer()?.uuid },
                      { signal }
                  )
              }
            : skipToken,
        enabled: !!orderId,
        ...options,
    })

    return { orderOverview: data, isOrderOverviewPending: isLoading }
}

const dateOrEmptyString = (maybeDate: Dayjs | null) => (maybeDate ? toIsoDateString(maybeDate) : '')

export function useGetOrderOverviews({
    statuses,
    assignedCustomers,
    titleFilter,
    valuationDateRange,
    portfolio,
}: {
    statuses?: string[]
    assignedCustomers?: number[]
    titleFilter?: string
    valuationDateRange?: DateRange
    portfolio?: PortfolioMetadata
} = {}) {
    const valuationApiClient = getValuationApiClient()

    const joinedStatuses = statuses ? statuses.join(',') : undefined
    const joinedAssignedCustomers = assignedCustomers ? assignedCustomers.join(',') : undefined

    const queryKey: string[] = [ORDER_OVERVIEWS_KEY]
    joinedStatuses && queryKey.push(`statusFilter: ${joinedStatuses}`)
    joinedAssignedCustomers && queryKey.push(`assignedCustomerFilter: ${joinedAssignedCustomers}`)
    titleFilter && queryKey.push(`titleFilter: ${titleFilter}`)
    valuationDateRange &&
        queryKey.push(`valuationDateRange: ${dateOrEmptyString(valuationDateRange.start)}-${dateOrEmptyString(valuationDateRange.end)}`)
    portfolio && queryKey.push(`portfolio: ${portfolio.uuid}`)
    const { data, isPending } = useQuery({
        // eslint-disable-next-line @tanstack/query/exhaustive-deps
        queryKey,
        queryFn: ({ signal }) =>
            valuationApiClient.orderOverviews.getClientPortalOrderOverviews(
                {
                    customerId: getSelectedCustomer()?.uuid,
                    statuses,
                    assignedCustomers,
                    titleFilter: titleFilter || undefined,
                    valuationDateRange,
                    portfolioId: portfolio?.uuid,
                },
                { signal }
            ),
    })

    // For now we are creating a bunch of dummy orders. Make sure to filter them out as long as we have them
    const filteredData = data ? { orders: data.orders.filter((order) => order.status !== 'temp_hide_in_portal') } : undefined
    return { orderOverviews: filteredData, isOrderOverviewsPending: isPending }
}

export function useCreateOrder({ onSuccess, onError }: ReactQueryOptions<ExistingOrder> = {}) {
    const queryClient = useQueryClient()
    const handleError = useGlobalMutationErrorHandler(onError)

    const { isPending, isError, error, mutate } = useMutation({
        mutationKey: [CREATE_ORDER_KEY],
        mutationFn: (newOrder: NewOrder) => getValuationApiClient().orders.postOrder(newOrder, { customerId: getSelectedCustomer()?.uuid }),
        onSuccess,
        meta: {
            invalidates: [[ORDERS_KEY], [ORDERS_WITH_PLAUSIBILITY_KEY]],
        },
        onMutate: async (newOrder) => {
            try {
                // Cancel any outgoing refetches
                // (so they don't overwrite our optimistic update)
                await queryClient.cancelQueries({ queryKey: [ORDERS_KEY] })

                // Snapshot the previous value
                const previousOrderOverviews = queryClient.getQueryData<OrderOverviews>([ORDERS_KEY])

                // Optimistically update to the new value
                queryClient.setQueryData([ORDERS_KEY], (previousOverviews: OrderOverviews | undefined) => {
                    if (!previousOverviews) {
                        return undefined
                    }
                    return { orders: [...previousOverviews.orders, newOrder] }
                })

                return { previousOrderOverviews }
            } catch (e) {
                sendToSentry(e)
            }
        },
        onError: (err, _, context) => {
            queryClient.setQueryData([ORDERS_KEY], context?.previousOrderOverviews)
            handleError(err)
        },
        onSettled: () => {
            void queryClient.invalidateQueries({
                predicate: (q) => q.queryKey[0] === ORDERS_KEY || q.queryKey[0] === ORDERS_WITH_PLAUSIBILITY_KEY,
            })
        },
    })

    return {
        isCreatingOrder: isPending,
        isCreateOrderError: isError,
        createOrderError: error,
        createOrderMutate: mutate,
    }
}

export function useCreateExternalParticipant({
    onSuccess,
    onError,
    orderId,
}: WithReactQueryOptions<{ orderId?: string }, ExternalParticipantWithProxyEmail> = {}) {
    const valuationApiClient = getValuationApiClient()
    const handleError = useGlobalMutationErrorHandler(onError)

    const { mutate, isPending } = useMutation({
        mutationFn: ({
            email,
            lastName,
            salutation,
            firstName,
            organization_name,
        }: {
            email: string
            lastName: string
            salutation: string
            firstName?: string
            organization_name?: string
        }) => {
            invariant(orderId, 'Cannot create external participant without orderId')
            return valuationApiClient.orders.postExternalParticipant({
                orderId,
                newExternalParticipant: { email, last_name: lastName, salutation, first_name: firstName, organization_name },
            })
        },
        onSuccess,
        onError: handleError,
        meta: {
            invalidates: [
                [EXTERNAL_PARTICIPANTS_KEY, orderId],
                [ORDER_OVERVIEWS_KEY, orderId],
                [ORDERS_KEY],
                [ORDERS_WITH_PLAUSIBILITY_KEY],
                THREAD_KEYS.all,
            ],
        },
    })

    return {
        createExternalParticipant: mutate,
        isCreatingExternalParticipant: isPending,
    }
}

export function useGetExternalParticipants({
    orderId,
    threadId,
    enabled = true,
    ...options
}: WithReactQueryOptions<{ orderId?: string; threadId?: string }, ExternalParticipantList> = {}) {
    const valuationApiClient = getValuationApiClient()
    const { data, isLoading, isPending } = useQuery({
        queryKey: [EXTERNAL_PARTICIPANTS_KEY, orderId, threadId],
        queryFn: orderId
            ? ({ signal }) =>
                  valuationApiClient.orders.getExternalParticipants(orderId, {
                      signal,
                      threadId,
                  })
            : skipToken,
        enabled: !!orderId && enabled,
        ...options,
    })

    return {
        externalParticipants: data,
        isExternalParticipantsInitialLoading: isLoading,
        isExternalParticipantsPending: isPending,
    }
}

export function useDeleteExternalParticipant({ onSuccess, onError, orderId }: WithReactQueryOptions<{ orderId?: string }, void> = {}) {
    const valuationApiClient = getValuationApiClient()
    const handleError = useGlobalMutationErrorHandler(onError)

    const { mutate: deleteExternalParticipant, isPending: isDeleteExternalParticipantPending } = useMutation({
        mutationFn: ({ participantId }: { participantId: string }) => {
            invariant(orderId, 'Cannot delete external participant without orderId')
            return valuationApiClient.orders.deleteExternalParticipant({ orderId, participantId })
        },
        onSuccess,
        onError: handleError,
        meta: {
            invalidates: [
                [EXTERNAL_PARTICIPANTS_KEY, orderId],
                [ORDER_OVERVIEWS_KEY, orderId],
                [ORDERS_KEY],
                [ORDERS_WITH_PLAUSIBILITY_KEY],
            ],
        },
    })

    return {
        deleteExternalParticipant,
        isDeleteExternalParticipantPending,
    }
}

export function useUploadOrderImages({ onSuccess, onError, orderId }: WithReactQueryOptions<{ orderId?: string }>) {
    const queryClient = useQueryClient()
    const handleError = useGlobalMutationErrorHandler(onError)

    const mutation = useUploadMultiple<File>({
        onSuccess: (data) => {
            onSuccess?.(data)
            void queryClient.invalidateQueries({
                queryKey: [ORDER_OVERVIEWS_KEY, orderId],
            })
        },
        onError: handleError,
        mutationFn: async (image: File) => {
            invariant(orderId, 'Cannot upload order image without orderId')
            await getValuationApiClient().orders.postImage(orderId, image, { customerId: getSelectedCustomer()?.uuid })
            return { status: 'fulfilled' }
        },
        errorMsgFn: (image: File) => image.name,
    })

    return {
        uploadOrderImages: mutation.uploadMultiple,
        isUploadingOrderImages: mutation.isUploading,
        uploadOrderImagesError: mutation.uploadError,
        failedImageUploads: mutation.failedUploads,
        numImagesToUpload: mutation.numToUpload,
    }
}

export function useUploadOrderDocuments({ onSuccess, onError, orderId }: WithReactQueryOptions<{ orderId?: string }>) {
    const queryClient = useQueryClient()
    const handleError = useGlobalMutationErrorHandler(onError)

    const mutation = useUploadMultiple<File>({
        onSuccess: (data) => {
            onSuccess?.(data)
            void queryClient.invalidateQueries({
                queryKey: [ORDER_OVERVIEWS_KEY, orderId],
            })
        },
        onError: handleError,
        mutationFn: async (document: File) => {
            invariant(orderId, 'Cannot upload order document without orderId')
            await getValuationApiClient().orders.postDocument(orderId, document, { customerId: getSelectedCustomer()?.uuid })
            return { status: 'fulfilled' }
        },
        errorMsgFn: (document: File) => document.name,
    })

    return {
        uploadOrderDocuments: mutation.uploadMultiple,
        isUploadingOrderDocuments: mutation.isUploading,
        uploadOrderDocumentsError: mutation.uploadError,
        failedDocumentUploads: mutation.failedUploads,
        numDocumentsToUpload: mutation.numToUpload,
    }
}

export function usePatchOrder({
    onSuccess,
    onError,
    orderId,
    shouldDelayInvalidation = false,
}: WithReactQueryOptions<{ orderId?: string }> & {
    shouldDelayInvalidation?: boolean
}) {
    const queryClient = useQueryClient()
    const handleError = useGlobalMutationErrorHandler(onError)

    const { isPending, isError, error, mutate, mutateAsync } = useMutation({
        mutationFn: (operations: Operation[]) => {
            invariant(orderId, 'Cannot patch order without orderId')
            return getValuationApiClient().orders.patchOrder({ orderId, operations, customerId: getSelectedCustomer()?.uuid })
        },
        onSuccess,
        onMutate: async (operations): Promise<{ previousQueries: Map<QueryKey, unknown> } | undefined> => {
            if (!orderId) {
                return undefined
            }

            try {
                await Promise.all([
                    queryClient.cancelQueries({ queryKey: [ORDERS_KEY] }),
                    queryClient.cancelQueries({ queryKey: [ORDERS_WITH_PLAUSIBILITY_KEY] }),
                    queryClient.cancelQueries({ queryKey: [ORDER_OVERVIEWS_KEY] }),
                ])

                const matchingQueries = queryClient.getQueriesData({
                    predicate: (q) =>
                        q.queryKey[0] === ORDERS_KEY ||
                        q.queryKey[0] === ORDERS_WITH_PLAUSIBILITY_KEY ||
                        q.queryKey[0] === ORDER_OVERVIEWS_KEY,
                })
                const previousQueries = new Map<QueryKey, unknown>()

                matchingQueries.forEach(([queryKey, data]) => {
                    previousQueries.set(queryKey, data)

                    if (data && typeof data === 'object' && 'orders' in data && Array.isArray((data as OrderOverviews).orders)) {
                        const updatedOrders = (data as OrderOverviews).orders.map((candidate) => {
                            if (!isOrderOverview(candidate) || candidate.uuid !== orderId) {
                                return candidate
                            }
                            return patchSingleDocument(candidate, operations)
                        })

                        queryClient.setQueryData(queryKey, { ...(data as OrderOverviews), orders: updatedOrders })
                    }
                })

                return { previousQueries }
            } catch (error) {
                sendToSentry(error)
                return undefined
            }
        },
        onError: (err, _variables, context?: { previousQueries: Map<QueryKey, unknown> }) => {
            if (context?.previousQueries) {
                context.previousQueries.forEach((data, queryKey) => {
                    queryClient.setQueryData(queryKey, data)
                })
            }
            handleError(err)
        },
        meta: {
            invalidates: shouldDelayInvalidation ? [] : [[ORDERS_KEY], [ORDERS_WITH_PLAUSIBILITY_KEY], [ORDER_OVERVIEWS_KEY]],
        },
        onSettled: () => {
            if (shouldDelayInvalidation) {
                if (orderOverviewsInvalidationTimer) {
                    clearTimeout(orderOverviewsInvalidationTimer)
                }
                orderOverviewsInvalidationTimer = setTimeout(() => {
                    void queryClient.invalidateQueries({
                        predicate: (q) =>
                            q.queryKey[0] === ORDERS_KEY ||
                            q.queryKey[0] === ORDERS_WITH_PLAUSIBILITY_KEY ||
                            q.queryKey[0] === ORDER_OVERVIEWS_KEY,
                    })
                    orderOverviewsInvalidationTimer = undefined
                }, ORDER_OVERVIEWS_INVALIDATION_DELAY_MS)
                return
            }
            void queryClient.invalidateQueries({
                predicate: (q) =>
                    q.queryKey[0] === ORDERS_KEY || q.queryKey[0] === ORDERS_WITH_PLAUSIBILITY_KEY || q.queryKey[0] === ORDER_OVERVIEWS_KEY,
            })
        },
    })

    return {
        isPatchingOrder: isPending,
        isPatchOrderError: isError,
        patchOrderError: error,
        patchOrder: mutate,
        patchOrderAsync: mutateAsync,
    }
}

const patchSingleDocument = <TDocument extends Record<string, unknown>>(document: TDocument, operations: Operation[]): TDocument => {
    try {
        const { newDocument } = applyPatch(document, operations, false, false)
        return newDocument
    } catch (error) {
        sendToSentry(error)
        return document
    }
}

export const useFetchNewOrderDocuments = () => {
    const handleError = useGlobalMutationErrorHandler()
    const { mutateAsync, isPending } = useMutation({
        mutationFn: ({ orderId }: { orderId: string }) =>
            getValuationApiClient().orders.getOrder(orderId, { customerId: getSelectedCustomer()?.uuid }),
        onError: handleError,
    })
    return { fetchNewOrderDocuments: mutateAsync, isFetchingNewOrderDocuments: isPending }
}

export const useFetchNewReportUrlFromValuation = () => {
    const handleError = useGlobalMutationErrorHandler()
    const { mutateAsync, isPending } = useMutation({
        mutationFn: getValuation,
        onError: handleError,
    })
    return { fetchNewReportUrl: mutateAsync, isFetchingNewReportUrl: isPending }
}

export function isOrderOverview(orderOverview: unknown): orderOverview is OrderOverview {
    return typeof orderOverview === 'object' && orderOverview !== null && 'uuid' in orderOverview
}
